import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  flexRender,
} from "@tanstack/react-table";
import { Icon } from "@iconify/react/dist/iconify.js";
import { Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import {
  getMatches,
  deleteMatch,
  createMatch,
  updateMatch,
} from "../../services/matchService";
import { getLeagues } from "../../services/leagueService";
import { getTeams } from "../../services/teamService";

const toastCfg = { autoClose: 1000 };

/* ─── Helpers ─── */
const formatForInput = (date) => {
  if (!date) return "";
  if (date.length === 10) return `${date}T00:00`;
  return date.replace(" ", "T").slice(0, 16);
};

const getStatusColor = (status) => {
  switch (status) {
    case "upcoming": return { bg: "#fef3c7", color: "#d97706", border: "#fde68a" };
    case "live": return { bg: "#dcfce7", color: "#16a34a", border: "#bbf7d0" };
    case "closed": return { bg: "#f1f5f9", color: "#64748b", border: "#e2e8f0" };
    default: return { bg: "#f1f5f9", color: "#475569", border: "#e2e8f0" };
  }
};

const SortIcon = ({ col }) => {
  if (!col.getCanSort()) return null;
  const dir = col.getIsSorted();
  return (
    <span style={{ marginLeft: 4, opacity: dir ? 1 : 0.35, fontSize: 11 }}>
      {dir === "asc" ? "▲" : dir === "desc" ? "▼" : "⇅"}
    </span>
  );
};

/* ─── Add/Edit Match Modal ─── */
const MatchModal = ({ show, onHide, onSuccess, modalType, editData, leagues, teams }) => {
  const [form, setForm] = useState({
    league_id: "",
    team_a: "",
    team_b: "",
    match_date: "",
    status: "upcoming",
    admin_risk: "",
  });
  const [teamAPreview, setTeamAPreview] = useState(null);
  const [teamBPreview, setTeamBPreview] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (show) {
      if (modalType === "edit" && editData) {
        setForm({
          league_id: editData.league_id || "",
          team_a: editData.team_a || "",
          team_b: editData.team_b || "",
          match_date: formatForInput(editData.match_date),
          status: editData.status || "upcoming",
          admin_risk: editData.admin_risk || "",
        });
        setTeamAPreview(editData.team_a_img || null);
        setTeamBPreview(editData.team_b_img || null);
      } else {
        setForm({ league_id: "", team_a: "", team_b: "", match_date: "", status: "upcoming", admin_risk: "" });
        setTeamAPreview(null);
        setTeamBPreview(null);
      }
    }
  }, [show, modalType, editData]);

  const filteredTeams = useMemo(() => {
    if (!form.league_id) return [];
    return teams.filter((t) => String(t.league_id) === String(form.league_id));
  }, [teams, form.league_id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "league_id") {
      setForm({ ...form, league_id: value, team_a: "", team_b: "" });
      setTeamAPreview(null);
      setTeamBPreview(null);
      return;
    }
    setForm({ ...form, [name]: value });
  };

  const handleTeamChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
    const selected = filteredTeams.find((t) => t.name === value);
    if (name === "team_a") setTeamAPreview(selected?.image_url || null);
    if (name === "team_b") setTeamBPreview(selected?.image_url || null);
  };

  const handleSubmit = async () => {
    if (!form.league_id || !form.team_a || !form.team_b) {
      toast.error("League, Team A, and Team B are required", toastCfg);
      return;
    }
    if (form.team_a === form.team_b) {
      toast.error("Team A and Team B cannot be the same", toastCfg);
      return;
    }

    const formData = new FormData();
    Object.entries(form).forEach(([key, value]) => {
      if (key === "match_date" && value) {
        formData.append("match_date", value.replace("T", " ") + ":00");
      } else {
        formData.append(key, value);
      }
    });
    formData.append("team_a_img", teamAPreview || "");
    formData.append("team_b_img", teamBPreview || "");

    setSaving(true);
    try {
      const res = modalType === "edit"
        ? await updateMatch(editData.id, formData)
        : await createMatch(formData);
      
      if (res?.status) {
        toast.success(modalType === "edit" ? "Match updated" : "Match created", toastCfg);
        onSuccess();
        onHide();
      } else {
        toast.error(res?.message || "Operation failed", toastCfg);
      }
    } catch {
      toast.error("Server error", toastCfg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered backdrop="static" size="lg">
      <div style={ms.header}>
        <div>
          <div style={ms.headerTitle}>{modalType === "add" ? "Create Match Overview" : "Modify Match Details"}</div>
          <div style={ms.headerSub}>{modalType === "add" ? "Set up a new live poll fixture" : "Change scheduled times, teams, or status risks"}</div>
        </div>
        <button style={ms.closeBtn} onClick={onHide} disabled={saving}>
          <Icon icon="radix-icons:cross-2" style={{ fontSize: 18 }} />
        </button>
      </div>
      <div style={ms.body}>
        <div style={ms.grid2x}>
          <div style={{ gridColumn: "1 / -1" }}>
            <label style={ms.label}>League Division</label>
            <select style={ms.select} name="league_id" value={form.league_id} onChange={handleChange} disabled={saving}>
              <option value="">Select League...</option>
              {leagues.map((l) => <option key={l.id} value={l.id}>{l.league_name}</option>)}
            </select>
          </div>

          <div>
            <label style={ms.label}>Team A</label>
            <select style={ms.select} name="team_a" value={form.team_a} onChange={handleTeamChange} disabled={!form.league_id || saving}>
              <option value="">Select Team A...</option>
              {filteredTeams.map((t) => <option key={t.id} value={t.name}>{t.name}</option>)}
            </select>
            {teamAPreview && <img src={teamAPreview} alt="Team A" style={{ width: 44, height: 44, marginTop: 10, borderRadius: 8, objectFit: "contain", border: "1px solid #e2e8f0" }} />}
          </div>

          <div>
            <label style={ms.label}>Team B</label>
            <select style={ms.select} name="team_b" value={form.team_b} onChange={handleTeamChange} disabled={!form.league_id || saving}>
              <option value="">Select Team B...</option>
              {filteredTeams.map((t) => <option key={t.id} value={t.name}>{t.name}</option>)}
            </select>
            {teamBPreview && <img src={teamBPreview} alt="Team B" style={{ width: 44, height: 44, marginTop: 10, borderRadius: 8, objectFit: "contain", border: "1px solid #e2e8f0" }} />}
          </div>

          <div style={{ gridColumn: "1 / -1", height: 1, background: "#f1f5f9", margin: "10px 0" }} />

          <div>
            <label style={ms.label}>Match Date & Time</label>
            <input type="datetime-local" style={ms.input} name="match_date" value={form.match_date} onChange={handleChange} disabled={saving} />
          </div>

          <div>
            <label style={ms.label}>Live Status</label>
            <select style={ms.select} name="status" value={form.status} onChange={handleChange} disabled={saving}>
              <option value="upcoming">Upcoming</option>
              <option value="live">Live</option>
              <option value="closed">Closed</option>
            </select>
          </div>

          <div style={{ gridColumn: "1 / -1" }}>
            <label style={ms.label}>Admin Risk Control (%)</label>
            <input style={ms.input} name="admin_risk" placeholder="Enter risk value..." value={form.admin_risk} onChange={handleChange} disabled={saving} />
          </div>
        </div>
      </div>
      <div style={ms.footer}>
        <button style={ms.cancelBtn} onClick={onHide} disabled={saving}>Cancel</button>
        <button style={ms.approveBtn} onClick={handleSubmit} disabled={saving}>
          {saving ? <span className="spinner-border spinner-border-sm" /> : modalType === "add" ? "Create Match" : "Save Changes"}
        </button>
      </div>
    </Modal>
  );
};

/* ─── Summary Modal ─── */
const ViewModal = ({ show, onHide, match }) => {
  if (!match) return null;
  const stat = getStatusColor(match.status);

  return (
    <Modal show={show} onHide={onHide} centered>
      <div style={{ ...ms.header, paddingBottom: 20 }}>
        <div>
          <div style={ms.headerTitle}>Match Overview</div>
          <div style={ms.headerSub}>Detailed fixture data</div>
        </div>
        <button style={ms.closeBtn} onClick={onHide}>
          <Icon icon="radix-icons:cross-2" style={{ fontSize: 18 }} />
        </button>
      </div>
      <div style={{ padding: "24px" }}>
        <div style={{ padding: "16px 20px", background: "#f8fafc", borderRadius: 12, border: "1px solid #e2e8f0", marginBottom: 20 }}>
          <div style={{ fontSize: "0.75rem", fontWeight: 700, color: "#64748b", textTransform: "uppercase", marginBottom: 4 }}>League</div>
          <div style={{ fontSize: "1rem", fontWeight: 700, color: "#0f172a" }}>{match.league_name}</div>
        </div>
        
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24, padding: "0 10px" }}>
          <div style={{ textAlign: "center", flex: 1 }}>
            {match.team_a_img ? (
              <img src={match.team_a_img} alt="A" style={{ width: 64, height: 64, objectFit: "contain", borderRadius: 12, marginBottom: 8, border: "1px solid #e2e8f0" }} />
            ) : <div style={{ width: 64, height: 64, background: "#f1f5f9", borderRadius: 12, margin: "0 auto 8px" }} />}
            <div style={{ fontWeight: 700, fontSize: "0.95rem", color: "#1e293b" }}>{match.team_a}</div>
          </div>
          
          <div style={{ flexShrink: 0, padding: "0 20px" }}>
            <div style={{ background: "#fef2f2", color: "#ef4444", fontWeight: 800, padding: "6px 14px", borderRadius: 100, fontSize: "0.85rem" }}>VS</div>
          </div>

          <div style={{ textAlign: "center", flex: 1 }}>
            {match.team_b_img ? (
              <img src={match.team_b_img} alt="B" style={{ width: 64, height: 64, objectFit: "contain", borderRadius: 12, marginBottom: 8, border: "1px solid #e2e8f0" }} />
            ) : <div style={{ width: 64, height: 64, background: "#f1f5f9", borderRadius: 12, margin: "0 auto 8px" }} />}
            <div style={{ fontWeight: 700, fontSize: "0.95rem", color: "#1e293b" }}>{match.team_b}</div>
          </div>
        </div>

        <div style={{ display: "flex", gap: "12px", borderTop: "1px solid #f1f5f9", paddingTop: 20 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: "0.75rem", fontWeight: 700, color: "#64748b", textTransform: "uppercase", marginBottom: 4 }}>Schedule</div>
            <div style={{ fontSize: "0.85rem", fontWeight: 600, color: "#334155" }}>{match.match_date}</div>
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: "0.75rem", fontWeight: 700, color: "#64748b", textTransform: "uppercase", marginBottom: 4 }}>Status</div>
            <span style={{ background: stat.bg, color: stat.color, border: `1px solid ${stat.border}`, padding: "4px 10px", borderRadius: 100, fontSize: "0.72rem", fontWeight: 700, textTransform: "uppercase", display: "inline-block" }}>
              {match.status}
            </span>
          </div>
        </div>
      </div>
    </Modal>
  );
};

/* ─── Layout Styles ─── */
const ms = {
  header: { display: "flex", alignItems: "flex-start", justifyContent: "space-between", padding: "20px 24px 16px", borderBottom: "1px solid #f1f5f9" },
  headerTitle: { fontWeight: 800, fontSize: "1.05rem", color: "#0f172a" },
  headerSub: { fontSize: "0.78rem", color: "#94a3b8", marginTop: 2 },
  closeBtn: { background: "#f1f5f9", border: "none", borderRadius: 8, width: 32, height: 32, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#64748b" },
  body: { padding: "18px 24px" },
  grid2x: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px 20px" },
  label: { display: "block", fontSize: "0.75rem", fontWeight: 700, color: "#475569", marginBottom: 5, textTransform: "uppercase", letterSpacing: "0.04em" },
  input: { width: "100%", padding: "9px 12px", border: "1.5px solid #e2e8f0", borderRadius: 9, fontSize: "0.87rem", color: "#1e293b", background: "#f8fafc", outline: "none", fontFamily: "inherit" },
  select: { width: "100%", padding: "9px 12px", border: "1.5px solid #e2e8f0", borderRadius: 9, fontSize: "0.87rem", color: "#1e293b", background: "#f8fafc", outline: "none", cursor: "pointer", fontFamily: "inherit" },
  footer: { display: "flex", justifyContent: "flex-end", gap: 10, padding: "14px 24px 20px", borderTop: "1px solid #f1f5f9" },
  cancelBtn: { padding: "8px 18px", background: "#f1f5f9", border: "none", borderRadius: 8, fontFamily: "inherit", fontSize: "0.85rem", fontWeight: 600, color: "#475569", cursor: "pointer" },
  approveBtn: { display: "flex", alignItems: "center", gap: 6, padding: "8px 20px", background: "linear-gradient(135deg,#16632b,#1da144)", border: "none", borderRadius: 8, fontFamily: "inherit", fontSize: "0.85rem", fontWeight: 700, color: "#fff", cursor: "pointer", minWidth: 100, justifyContent: "center" },
};

const s = {
  root: { padding: "20px 24px 40px", background: "#f1f5f9", minHeight: "100vh", fontFamily: "'Inter', sans-serif" },
  headerWrap: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 },
  pageTitle: { margin: 0, fontWeight: 800, fontSize: "1.5rem", color: "#0f172a" },
  pageSub: { margin: "4px 0 0", color: "#64748b", fontSize: "0.85rem", display: "flex", alignItems: "center", gap: 8 },
  addBtn: { display: "flex", alignItems: "center", gap: 6, padding: "9px 18px", background: "linear-gradient(135deg,#16632b,#1da144)", border: "none", borderRadius: 9, fontFamily: "inherit", fontSize: "0.85rem", fontWeight: 700, color: "#fff", cursor: "pointer", boxShadow: "0 2px 6px rgba(22,99,43,0.3)" },
  card: { background: "#fff", borderRadius: 16, border: "1px solid #e8edf2", boxShadow: "0 1px 4px rgba(0,0,0,0.04)", overflow: "hidden" },
  toolbar: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", borderBottom: "1px solid #f1f5f9", flexWrap: "wrap", gap: 10 },
  toolbarLeft: { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" },
  toolbarRight: { display: "flex", alignItems: "center", gap: 8 },
  searchWrap: { position: "relative", display: "flex", alignItems: "center" },
  searchIcon: { position: "absolute", left: 10, fontSize: 15, color: "#94a3b8", pointerEvents: "none" },
  searchInput: { padding: "7px 30px 7px 32px", border: "1.5px solid #e2e8f0", borderRadius: 9, fontSize: "0.82rem", color: "#334155", background: "#f8fafc", outline: "none", width: 220, fontFamily: "inherit" },
  clearBtn: { position: "absolute", right: 8, background: "none", border: "none", cursor: "pointer", color: "#94a3b8", padding: 0, display: "flex", alignItems: "center" },
  select: { padding: "7px 10px", border: "1.5px solid #e2e8f0", borderRadius: 9, fontSize: "0.81rem", color: "#475569", background: "#f8fafc", outline: "none", cursor: "pointer", fontFamily: "inherit", minWidth: 120 },
  tableWrap: { overflowX: "auto" },
  table: { width: "100%", borderCollapse: "collapse", fontSize: "0.82rem" },
  th: { padding: "10px 14px", background: "#f8fafc", color: "#64748b", fontWeight: 700, fontSize: "0.71rem", textTransform: "uppercase", letterSpacing: "0.05em", borderBottom: "1.5px solid #f1f5f9", whiteSpace: "nowrap", textAlign: "left" },
  td: { padding: "9px 14px", borderBottom: "1px solid #f8fafc", color: "#334155", verticalAlign: "middle" },
  emptyCell: { padding: "48px 20px", textAlign: "center", color: "#94a3b8", fontSize: "0.85rem" },
  pagination: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 16px", borderTop: "1px solid #f1f5f9", flexWrap: "wrap", gap: 8 },
  pgInfo: { fontSize: "0.78rem", color: "#64748b" },
  pgBtns: { display: "flex", gap: 4 },
  pgBtn: { minWidth: 30, height: 30, border: "1px solid #e2e8f0", background: "#fff", borderRadius: 7, cursor: "pointer", fontSize: "0.78rem", color: "#475569", fontFamily: "inherit", display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.15s" },
  pgBtnActive: { background: "linear-gradient(135deg,#16632b,#1da144)", color: "#fff", border: "none", fontWeight: 700 },
};

const ts = {
  editBtn: { display: "inline-flex", alignItems: "center", gap: 4, padding: "4px 8px", background: "#eff6ff", color: "#2563eb", border: "1px solid #bfdbfe", borderRadius: 7, cursor: "pointer", fontFamily: "inherit", fontSize: "0.75rem", fontWeight: 600 },
  viewBtn: { display: "inline-flex", alignItems: "center", gap: 4, padding: "4px 8px", background: "#fdf4ff", color: "#c026d3", border: "1px solid #fbcfe8", borderRadius: 7, cursor: "pointer", fontFamily: "inherit", fontSize: "0.75rem", fontWeight: 600 },
  delBtn: { display: "inline-flex", alignItems: "center", gap: 4, padding: "4px 8px", background: "#fef2f2", color: "#dc2626", border: "1px solid #fecaca", borderRadius: 7, cursor: "pointer", fontFamily: "inherit", fontSize: "0.75rem", fontWeight: 600 },
};

export const MatchListLayer = () => {
  const [data, setData] = useState([]);
  const [leagues, setLeagues] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(false);

  const [leagueFilter, setLeagueFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [globalFilter, setGlobalFilter] = useState("");
  
  const [sorting, setSorting] = useState([]);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  
  // Modals
  const [showFormModal, setShowFormModal] = useState(false);
  const [modalType, setModalType] = useState("add");
  const [activeItem, setActiveItem] = useState(null);
  
  const [showViewModal, setShowViewModal] = useState(false);

  const loadDependencies = useCallback(async () => {
    try {
      const [lRes, tRes] = await Promise.all([getLeagues(), getTeams()]);
      if (lRes?.status) setLeagues(lRes.data || []);
      if (tRes?.status) setTeams(tRes.data || []);
    } catch (e) {
      console.error(e);
    }
  }, []);

  const loadMatches = useCallback(async (isSilent = false) => {
    if (!isSilent) setLoading(true);
    try {
      const res = await getMatches();
      if (res?.status === true) setData(res.data || []);
      else if (!isSilent) toast.error("Failed to load matches", toastCfg);
    } catch {
      if (!isSilent) toast.error("Server error", toastCfg);
    } finally {
      if (!isSilent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMatches();
    loadDependencies();
    const interval = setInterval(() => loadMatches(true), 5000);
    return () => clearInterval(interval);
  }, [loadMatches, loadDependencies]);

  const openAddModal = () => {
    setModalType("add");
    setActiveItem(null);
    setShowFormModal(true);
  };

  const openEditModal = (item) => {
    setModalType("edit");
    setActiveItem(item);
    setShowFormModal(true);
  };

  const openViewModal = (item) => {
    setActiveItem(item);
    setShowViewModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this match?")) return;
    try {
      const res = await deleteMatch(id);
      if (res?.status === true) {
        toast.success("Match deleted", toastCfg);
        loadMatches(true);
      } else {
        toast.error("Delete failed", toastCfg);
      }
    } catch {
      toast.error("Operation failed", toastCfg);
    }
  };

  const filteredData = useMemo(() => {
    return data.filter((m) => {
      const lMatch = leagueFilter ? String(m.league_id) === String(leagueFilter) : true;
      const sMatch = statusFilter ? m.status === statusFilter : true;
      return lMatch && sMatch;
    });
  }, [data, leagueFilter, statusFilter]);

  const columns = useMemo(() => [
    {
      id: "serial",
      header: "#",
      size: 50,
      enableSorting: false,
      cell: ({ row }) => <span style={{ color: "#64748b" }}>{row.index + 1 + pagination.pageIndex * pagination.pageSize}</span>,
    },
    {
      accessorKey: "league_name",
      header: "League",
      cell: ({ getValue }) => <div style={{ fontWeight: 600, color: "#475569", fontSize: "0.78rem" }}>{getValue() || "—"}</div>,
    },
    {
      id: "matchup",
      header: "Matchup",
      size: 350,
      cell: ({ row }) => (
        <div style={{ display: "flex", alignItems: "center", gap: 8, whiteSpace: "nowrap" }}>
          <span style={{ fontWeight: 700, color: "#1e293b", fontSize: "0.85rem" }}>{row.original.team_a}</span>
          <span style={{ fontSize: "0.7rem", color: "#ef4444", fontWeight: 800 }}>VS</span>
          <span style={{ fontWeight: 700, color: "#1e293b", fontSize: "0.85rem" }}>{row.original.team_b}</span>
        </div>
      ),
    },
    {
      accessorKey: "match_date",
      header: "Schedule",
      cell: ({ getValue }) => <span style={{ fontSize: "0.78rem" }}>{getValue() || "—"}</span>,
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ getValue }) => {
        const val = getValue();
        const stat = getStatusColor(val);
        return (
          <span style={{ background: stat.bg, color: stat.color, border: `1px solid ${stat.border}`, padding: "3px 9px", borderRadius: 100, fontSize: "0.68rem", fontWeight: 700, textTransform: "uppercase" }}>
            {val || "—"}
          </span>
        );
      },
    },
    {
      id: "actions",
      header: "Action",
      size: 190,
      enableSorting: false,
      cell: ({ row }) => (
        <div style={{ display: "flex", gap: 6 }}>
          <button style={ts.viewBtn} onClick={() => openViewModal(row.original)}>
            <Icon icon="solar:eye-bold" style={{ fontSize: 13 }} /> View
          </button>
          <button style={ts.editBtn} onClick={() => openEditModal(row.original)}>
            <Icon icon="solar:pen-bold" style={{ fontSize: 13 }} /> Edit
          </button>
          <button style={ts.delBtn} onClick={() => handleDelete(row.original.id)}>
            <Icon icon="solar:trash-bin-trash-bold" style={{ fontSize: 13 }} /> Del
          </button>
        </div>
      ),
    },
  ], [pagination.pageIndex, pagination.pageSize]);

  const table = useReactTable({
    data: filteredData,
    columns,
    state: { globalFilter, sorting, pagination },
    onGlobalFilterChange: setGlobalFilter,
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    globalFilterFn: (row, _colId, filterValue) => {
      const v = filterValue.toLowerCase();
      return (
        row.original.league_name?.toLowerCase().includes(v) ||
        row.original.team_a?.toLowerCase().includes(v) ||
        row.original.team_b?.toLowerCase().includes(v)
      );
    },
  });

  const { pageIndex, pageSize } = table.getState().pagination;
  const pageCount = table.getPageCount();
  const filteredDataLength = table.getFilteredRowModel().rows.length;

  return (
    <div style={s.root}>
      <div style={s.headerWrap}>
        <div>
          <h2 style={s.pageTitle}>Match Fixtures</h2>
          <p style={s.pageSub}>
             Control live polling match schedules
            <span style={{ background: "#eff6ff", color: "#2563eb", padding: "2px 8px", borderRadius: 100, fontSize: "0.65rem", fontWeight: 700, marginLeft: 6 }}>
              LIVE SYNC
            </span>
          </p>
        </div>
        <button style={s.addBtn} onClick={openAddModal}>
          <Icon icon="solar:flag-2-bold" style={{ fontSize: 18 }} />
          Create Match
        </button>
      </div>

      <div style={s.card}>
        <div style={s.toolbar}>
          <div style={s.toolbarLeft}>
            <div style={s.searchWrap}>
              <Icon icon="ion:search-outline" style={s.searchIcon} />
              <input
                style={s.searchInput}
                placeholder="Search teams or league…"
                value={globalFilter}
                onChange={(e) => { setGlobalFilter(e.target.value); setPagination((p) => ({ ...p, pageIndex: 0 })); }}
              />
              {globalFilter && (
                <button style={s.clearBtn} onClick={() => setGlobalFilter("")}>
                  <Icon icon="radix-icons:cross-2" style={{ fontSize: 12 }} />
                </button>
              )}
            </div>

            <select style={s.select} value={leagueFilter}
              onChange={(e) => { setLeagueFilter(e.target.value); setPagination((p) => ({ ...p, pageIndex: 0 })); }}>
              <option value="">All Leagues</option>
              {leagues.map((l) => (
                <option key={l.id} value={l.id}>{l.league_name}</option>
              ))}
            </select>

            <select style={s.select} value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setPagination((p) => ({ ...p, pageIndex: 0 })); }}>
              <option value="">All Statuses</option>
              <option value="upcoming">Upcoming</option>
              <option value="live">Live</option>
              <option value="closed">Closed</option>
            </select>
          </div>

          <div style={s.toolbarRight}>
            <select style={s.select} value={pageSize}
              onChange={(e) => table.setPageSize(Number(e.target.value))}>
              {[10, 25, 50, 100].map((n) => (
                <option key={n} value={n}>Show {n}</option>
              ))}
            </select>
          </div>
        </div>

        <div style={s.tableWrap}>
          <table style={s.table}>
            <thead>
              {table.getHeaderGroups().map((hg) => (
                <tr key={hg.id}>
                  {hg.headers.map((header) => (
                    <th
                      key={header.id}
                      style={{
                        ...s.th,
                        width: header.column.getSize() || undefined,
                        cursor: header.column.getCanSort() ? "pointer" : "default",
                        userSelect: "none",
                      }}
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      <SortIcon col={header.column} />
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={columns.length} style={s.emptyCell}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, justifyContent: "center", color: "#94a3b8" }}>
                      <span className="spinner-border spinner-border-sm" />
                      Loading…
                    </div>
                  </td>
                </tr>
              ) : table.getRowModel().rows.length === 0 ? (
                <tr>
                  <td colSpan={columns.length} style={s.emptyCell}>
                    <Icon icon="solar:clipboard-list-bold-duotone" style={{ fontSize: 36, color: "#cbd5e1", display: "block", margin: "0 auto 8px" }} />
                    No match fixtures found
                  </td>
                </tr>
              ) : (
                table.getRowModel().rows.map((row, idx) => (
                  <tr key={row.id}
                    style={{ background: idx % 2 === 0 ? "#fff" : "#fafbfc" }}
                    onMouseEnter={(e) => e.currentTarget.style.background = "#f0fdf4"}
                    onMouseLeave={(e) => e.currentTarget.style.background = idx % 2 === 0 ? "#fff" : "#fafbfc"}>
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id} style={s.td}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div style={s.pagination}>
          <div style={s.pgInfo}>
            Showing{" "}
            <strong>{filteredDataLength > 0 ? pageIndex * pageSize + 1 : 0}–{Math.min((pageIndex + 1) * pageSize, filteredDataLength)}</strong>
            {" "}of <strong>{filteredDataLength}</strong> records
          </div>
          <div style={s.pgBtns}>
            <button style={s.pgBtn} onClick={() => table.setPageIndex(0)}          disabled={!table.getCanPreviousPage()}>«</button>
            <button style={s.pgBtn} onClick={() => table.previousPage()}            disabled={!table.getCanPreviousPage()}>‹</button>
            {Array.from({ length: Math.min(pageCount, 7) }, (_, i) => {
              const MathMax = Math.max;
              const MathMin = Math.min;
              const pg = MathMax(0, MathMin(pageIndex - 3, pageCount - 7)) + i;
              if (pg >= pageCount) return null;
              return (
                <button key={pg} style={{ ...s.pgBtn, ...(pg === pageIndex ? s.pgBtnActive : {}) }}
                  onClick={() => table.setPageIndex(pg)}>
                  {pg + 1}
                </button>
              );
            })}
            <button style={s.pgBtn} onClick={() => table.nextPage()}                disabled={!table.getCanNextPage()}>›</button>
            <button style={s.pgBtn} onClick={() => table.setPageIndex(pageCount - 1)} disabled={!table.getCanNextPage()}>»</button>
          </div>
        </div>
      </div>

      <MatchModal 
        show={showFormModal} 
        onHide={() => setShowFormModal(false)} 
        onSuccess={() => loadMatches(true)} 
        modalType={modalType}
        editData={activeItem}
        leagues={leagues}
        teams={teams}
      />

      <ViewModal 
        show={showViewModal}
        onHide={() => setShowViewModal(false)}
        match={activeItem}
      />
    </div>
  );
};